/**
 * DocGroupCoverageView — the Documents tab's "Document Group Coverage" sub-view.
 *
 * Where the Uploaded tab is a flat, file-first table, this is TAXONOMY-first:
 * it renders the complete doc-group → doc-type catalog the platform tracks and
 * overlays the loan's uploaded files onto it, so a gap is as visible as what
 * exists. One question, at a glance: across every group and type, what does
 * this loan actually have?
 *
 * Data:
 *  - catalog  → `useFileDocGroupAndTypesHook().fileDocGroupAndTypes` (the same
 *               group → [types] map the upload pickers use; fetched at
 *               dashboard bootstrap, fallback constant until then).
 *  - uploads  → `state.loanDrilldown.loanDocuments`, hidden files excluded,
 *               keyed "group|type". A doc whose group/type isn't in the
 *               catalog is appended to it rather than dropped, so the "Files
 *               uploaded" stat always equals the loan's non-hidden doc count.
 *
 * Upload affordances (the dashed "Upload Document" pill on an empty type and
 * "+ Add new" under a file list) open the OS picker and stage the chosen files
 * into the shared bottom-docked upload queue with docGroup/docType already
 * set — the same path the Missing tab's rows use — so the operator skips the
 * queue-then-classify step.
 *
 * "Missing" here means "no file uploaded", not "required and absent" — the
 * Missing tab owns requirements.
 */
import CloseRounded from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import UnfoldLessRounded from "@mui/icons-material/UnfoldLessRounded";
import UnfoldMoreRounded from "@mui/icons-material/UnfoldMoreRounded";
import {
  Box,
  ButtonBase,
  IconButton,
  Checkbox,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { isDocHidden } from "../../../../_src_shared/utils/functions/isDocHidden";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { getExpirationDaysForDocType } from "../../../../constants/documents/docTypeExpirationDurations";
import { useFileDocGroupAndTypesHook } from "../../../../hooks/useFileDocGroupAndTypesHook";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { slate, teal } from "../../../../theme/colors";
// The row / file / token primitives are shared with any card that renders ONE
// group's coverage inline (the Contacts tab's General Contractor card).
import {
  EMPTY_DOT,
  Highlight,
  INK_2,
  INK_3,
  LINE,
  LINE_SOFT,
  TypeRow,
  WARN_COLOR,
  keyOf,
} from "../../shared/docGroupCoverage";
import { useUploadQueue } from "./UploadQueueContext";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";

// The tab page background the shell header sits on (the sticky toolbar
// matches it so it reads as part of the page, not a strip).
// The drilldown's own page gray (LoanDrilldownScreen paints slate-100 behind
// every tab) — the sticky toolbar has to be opaque in the SAME gray, or it
// reads as a band when it pins.
const SHELL_BG = slate[100];

// ---------------------------------------------------------------------------
// Scope membership: which doc groups relate to a borrower / entity / property.
// Transaction-level groups (Closing Package, Title Docs, Terms & Sizer, Memos,
// Draw History, …) are deliberately unlisted and only show under All groups.
// Moving a group or adding a fourth scope is a one-line change here.
// ---------------------------------------------------------------------------
const SCOPES = {
  borrower: [
    "Borrower Documents",
    "Borrower Reports",
    "Experience and Track Record",
    "Letter of Explanation & Exceptions",
    "Liquidity",
  ],
  entity: ["Entity Docs", "Related Entity Docs"],
  property: [
    "Property Documents",
    "Leases & Rents",
    "Appraisal",
    "Insurance Docs",
    "Construction Documents",
    "Third Party Reports",
    "Questionnaires",
  ],
};

const SCOPE_OPTIONS = [
  { key: "all", label: "All Groups" },
  { key: "entity", label: "Entity Groups" },
  { key: "borrower", label: "Applicant Groups" },
  { key: "property", label: "Property Groups" },
];

// "Show missing types" — a bordered checkbox chip the same 32px height as the
// expand / collapse buttons beside it, with a count of the types it reveals.
const ShowMissingToggle = ({ count, checked, onChange }) => (
  <ButtonBase
    role="checkbox"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    sx={{
      height: 32,
      gap: 1,
      pl: 0.75,
      pr: 1,
      borderRadius: "8px",
      border: `1px solid ${checked ? teal[200] : LINE}`,
      background: checked ? teal[50] : "#fff",
      boxShadow: CARD_SHADOW,
      fontFamily: "inherit",
      transition: "border-color .12s, background .12s",
      "&:hover": { borderColor: checked ? teal[300] : slate[300] },
    }}
  >
    <Checkbox
      size="small"
      checked={checked}
      tabIndex={-1}
      disableRipple
      sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } }}
    />
    <Txt sx={{ fontSize: 12.5, color: INK_2, whiteSpace: "nowrap" }}>
      Show missing types
    </Txt>
    <Box
      component="span"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        minWidth: 22,
        textAlign: "center",
        borderRadius: "5px",
        px: 0.75,
        lineHeight: "18px",
        color: slate[700],
        background: LINE_SOFT,
      }}
    >
      {count}
    </Box>
  </ButtonBase>
);

const CARD_SHADOW = "0 1px 2px rgba(15,23,42,.05)";

// ---------------------------------------------------------------------------
// Header stat tile
// ---------------------------------------------------------------------------
const Stat = ({ value, of, label }) => (
  <Box
    sx={{
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "10px",
      px: 1.75,
      py: 1,
      minWidth: 108,
      boxShadow: CARD_SHADOW,
    }}
  >
    <Flx ac g={0.5} sx={{ lineHeight: 1.15 }}>
      <Txt sx={{ fontSize: 18, fontWeight: 700, color: slate[900] }}>
        {value}
      </Txt>
      {of != null && (
        <Txt sx={{ fontSize: 14, fontWeight: 500, color: INK_3 }}>/ {of}</Txt>
      )}
    </Flx>
    <Txt sx={{ fontSize: 11, color: INK_3 }}>{label}</Txt>
  </Box>
);

const LegendDot = ({ color, label }) => (
  <Flx ac g={0.75}>
    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: color }} />
    <Txt sx={{ fontSize: 11.5, color: INK_3 }}>{label}</Txt>
  </Flx>
);

// Toolbar pill — the two status chips and the scope button share one look.
const pillSx = (on) => ({
  border: `1px solid ${on ? teal[200] : LINE}`,
  background: on ? teal[100] : "#fff",
  color: on ? teal[700] : INK_2,
  fontWeight: on ? 600 : 500,
  fontSize: 12.5,
  px: 1.5,
  py: 0.875,
  borderRadius: "999px",
  boxShadow: CARD_SHADOW,
  gap: 0.75,
  lineHeight: 1.2,
  fontFamily: "inherit",
  transition: "border-color .12s, background .12s",
  "&:hover": {
    borderColor: on ? teal[300] : slate[300],
    color: on ? teal[700] : slate[900],
  },
});

// ---------------------------------------------------------------------------
// Group card
// ---------------------------------------------------------------------------
const GroupCard = ({
  group,
  visibleTypes,
  term,
  collapsed,
  onToggleCollapsed,
  openTypes,
  onToggleType,
  onUpload,
  onPreview,
}) => {
  const withDocs = group.types.filter((t) => t.files.length).length;
  const expandableTypeKeys = visibleTypes
    .filter((type) => type.files.length)
    .map((type) => keyOf(group.name, type.name));
  const allTypesOpen =
    expandableTypeKeys.length > 0 &&
    expandableTypeKeys.every((key) => openTypes.has(key));
  const pct = group.types.length
    ? Math.round((withDocs / group.types.length) * 100)
    : 0;
  const hasAny = withDocs > 0;
  return (
    <Box
      sx={{
        breakInside: "avoid",
        background: "#fff",
        border: `1px solid ${LINE}`,
        borderRadius: "10px",
        boxShadow: CARD_SHADOW,
        mb: 1.75,
        overflow: "hidden",
        // Muting is the mechanism: empty groups fall back so populated ones
        // carry the color and the eye finds them first.
        opacity: hasAny ? 1 : 0.55,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={onToggleCollapsed}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleCollapsed();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.75,
          py: 1.375,
          cursor: "pointer",
          userSelect: "none",
          outline: "none",
          "&:hover": { background: slate[50] },
          "&:focus-visible": { boxShadow: `inset 0 0 0 2px ${teal[300]}` },
          "&:hover .group-type-toggle, &:focus-within .group-type-toggle": {
            opacity: 1,
            pointerEvents: "auto",
          },
        }}
      >
        <Txt
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            fontWeight: 600,
            color: slate[900],
          }}
        >
          <Highlight text={group.name} term={term} />
        </Txt>
        {!!expandableTypeKeys.length && (
          <Tooltip
            title={
              allTypesOpen
                ? "Close all document types"
                : "Open all document types"
            }
          >
            <IconButton
              className="group-type-toggle"
              size="small"
              aria-label={
                allTypesOpen
                  ? `Close all ${group.name} document types`
                  : `Open all ${group.name} document types`
              }
              onClick={(e) => {
                e.stopPropagation();
                onToggleType(expandableTypeKeys, !allTypesOpen);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                width: 26,
                height: 26,
                flex: "none",
                opacity: 0,
                pointerEvents: "none",
                color: INK_3,
                transition: "opacity .15s, color .15s, background-color .15s",
                "&:focus-visible": { opacity: 1, pointerEvents: "auto" },
                "&:hover": { color: teal[700], background: teal[100] },
              }}
            >
              {allTypesOpen ? (
                <UnfoldLessRounded sx={{ fontSize: 17 }} />
              ) : (
                <UnfoldMoreRounded sx={{ fontSize: 17 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        <Box
          component="span"
          sx={{
            flex: "none",
            fontSize: 11.5,
            fontWeight: 600,
            borderRadius: "999px",
            px: 1.125,
            py: "2px",
            color: hasAny ? teal[700] : INK_2,
            background: hasAny ? teal[100] : LINE_SOFT,
          }}
        >
          {withDocs} of {group.types.length}
        </Box>
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 18,
            color: INK_3,
            flex: "none",
            transition: "transform .15s",
            transform: collapsed ? "rotate(-90deg)" : "none",
          }}
        />
      </Box>
      {!collapsed && (
        <>
          <Box
            sx={{
              height: 3,
              mx: 1.75,
              borderRadius: "2px",
              background: LINE_SOFT,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${pct}%`,
                background: teal[500],
                borderRadius: "2px",
                transition: "width .3s",
              }}
            />
          </Box>
          <Box sx={{ pt: 1, pb: 1.25, px: 0.75 }}>
            {/* Every type was hidden by "Missing Types" — say so rather than
                leaving an empty card body. */}
            {!visibleTypes.length && (
              <Txt
                component="div"
                sx={{ fontSize: 12, color: INK_3, px: 1, py: 0.5 }}
              >
                {hasAny
                  ? "No uploaded types match the search"
                  : "No documents uploaded in this group"}
              </Txt>
            )}
            {visibleTypes.map((t) => (
              <TypeRow
                key={t.name}
                type={t}
                term={t.termForRow}
                open={openTypes.has(keyOf(group.name, t.name))}
                onToggle={() => onToggleType(keyOf(group.name, t.name))}
                onUpload={(type) => onUpload(group.name, type.name)}
                onPreview={onPreview}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// The model — catalog + the loan's non-hidden docs merged. Shared by the tab
// and the drilldown's Documents quick-view sidebar so both count the same.
// ---------------------------------------------------------------------------
export const useDocCoverageModel = () => {
  const loanDocuments = useSelector((state) => state.loanDrilldown?.loanDocuments);
  const { fileDocGroupAndTypes } = useFileDocGroupAndTypesHook();

  const model = useMemo(() => {
    const catalog = {};
    Object.entries(fileDocGroupAndTypes || {}).forEach(([g, types]) => {
      catalog[g] = [...(types || [])];
    });
    const uploads = {};
    (loanDocuments || []).forEach((doc) => {
      if (isDocHidden(doc)) return;
      const g = doc?.docGroup || "Uncategorized";
      const t = doc?.docType || "Uncategorized";
      // Keep every file on the board even when its group/type isn't in the
      // catalog (legacy values, typos) — the stats must reconcile to the loan.
      if (!catalog[g]) catalog[g] = [];
      if (!catalog[g].includes(t)) catalog[g].push(t);
      const k = keyOf(g, t);
      if (!uploads[k]) uploads[k] = [];
      uploads[k].push(doc);
    });
    return Object.entries(catalog).map(([name, types]) => ({
      name,
      types: types.map((t) => {
        const files = uploads[keyOf(name, t)] || [];
        const warn = files.some((f) => f.documentStatus === "Rejected")
          ? "rejected"
          : files.some((f) => f.documentStatus === "Incomplete")
            ? "incomplete"
            : null;
        return { name: t, files, warn };
      }),
    }));
  }, [fileDocGroupAndTypes, loanDocuments]);

  // Header stats (whole loan, unfiltered).
  const stats = useMemo(() => {
    let totalTypes = 0;
    let typesWith = 0;
    let files = 0;
    model.forEach((g) =>
      g.types.forEach((t) => {
        totalTypes += 1;
        if (t.files.length) {
          typesWith += 1;
          files += t.files.length;
        }
      }),
    );
    return {
      totalGroups: model.length,
      groupsWith: model.filter((g) => g.types.some((t) => t.files.length))
        .length,
      totalTypes,
      typesWith,
      files,
    };
  }, [model]);

  return { model, stats };
};

/** The four-dot legend that explains the row colors. */
export const DocCoverageLegend = ({ sx }) => (
  <Flx ac wrap g={2} sx={sx}>
    <LegendDot color={teal[500]} label="Has uploads" />
    <LegendDot color={EMPTY_DOT} label="No uploads yet" />
    <LegendDot color={WARN_COLOR.rejected} label="Contains a rejected file" />
    <LegendDot
      color={WARN_COLOR.incomplete}
      label="Contains an incomplete file"
    />
  </Flx>
);

// ---------------------------------------------------------------------------
// The board — sticky toolbar (search, scope, hide switches, expand/collapse)
// over the masonry of group cards. `compact` stacks the toolbar for a narrow
// container (the quick-view sidebar); the masonry already falls to one column
// when there's no room for two.
// ---------------------------------------------------------------------------
export const DocGroupCoverageBoard = ({
  model,
  onUpload,
  onPreview,
  compact = false,
  toolbarBg = SHELL_BG,
}) => {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("all");
  // Off (the default) = only what's on file: types with no uploads are hidden,
  // and so is a group left with nothing in it. On = the whole catalog.
  const [showMissing, setShowMissing] = useState(false);
  const hideMissingTypes = !showMissing;
  const hideEmpty = !showMissing;
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [openTypes, setOpenTypes] = useState(() => new Set());

  const inScope = useCallback(
    (groupName) => scope === "all" || SCOPES[scope]?.includes(groupName),
    [scope],
  );

  // What each hide switch would take off the board, in the current scope:
  // groups with no uploads at all / types with no uploads.
  const hiddenCounts = useMemo(() => {
    let groups = 0;
    let types = 0;
    model.forEach((g) => {
      if (!inScope(g.name)) return;
      const missing = g.types.filter((t) => !t.files.length).length;
      types += missing;
      if (missing === g.types.length) groups += 1;
    });
    return { groups, types };
  }, [model, inScope]);

  // ---- filtered groups ----------------------------------------------------
  const term = query.trim().toLowerCase();
  const visibleGroups = useMemo(
    () =>
      model
        .map((g) => {
          if (!inScope(g.name)) return null;
          const hasAny = g.types.some((t) => t.files.length);
          if (hideEmpty && !hasAny) return null;
          const groupHit = !!term && g.name.toLowerCase().includes(term);
          // Search decides whether the GROUP is on the board at all.
          const matching = g.types.filter(
            (t) => !term || groupHit || t.name.toLowerCase().includes(term),
          );
          if (!matching.length) return null;
          // Hiding missing types only thins the rows — a group with no
          // uploads stays on the board as "0 of N" (that's what Missing
          // Groups is for).
          const types = matching
            .filter((t) => !(hideMissingTypes && !t.files.length))
            // When the GROUP name matched, its types stay visible but only
            // highlight when they match on their own.
            .map((t) => ({
              ...t,
              termForRow:
                groupHit && !t.name.toLowerCase().includes(term) ? "" : term,
            }));
          return { group: g, types };
        })
        .filter(Boolean),
    [model, inScope, hideEmpty, term, hideMissingTypes],
  );

  // ---- expand / collapse --------------------------------------------------
  const toggleGroup = (name) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  const toggleType = (keyOrKeys, open) =>
    setOpenTypes((prev) => {
      const next = new Set(prev);
      const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
      keys.forEach((key) => {
        if (open === true) next.add(key);
        else if (open === false) next.delete(key);
        else next.has(key) ? next.delete(key) : next.add(key);
      });
      return next;
    });
  const expandAll = () => {
    setCollapsedGroups(new Set());
    const all = new Set();
    model.forEach((g) =>
      g.types.forEach((t) => {
        if (t.files.length) all.add(keyOf(g.name, t.name));
      }),
    );
    setOpenTypes(all);
  };
  const collapseAll = () => {
    setCollapsedGroups(new Set(model.map((g) => g.name)));
    setOpenTypes(new Set());
  };

  // Lone search box — no form to align a label to, so the placeholder is its
  // label.
  const search = (
    <TextField
      size="small"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      placeholder="Search groups & document types…"
      autoComplete="off"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchOutlined sx={{ fontSize: 16, color: INK_3 }} />
          </InputAdornment>
        ),
        endAdornment: query ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => setQuery("")}
              sx={{ p: 0.25 }}
            >
              <CloseRounded sx={{ fontSize: 14 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        flex: 1,
        minWidth: 200,
        maxWidth: compact ? "none" : 300,
        width: compact ? "100%" : undefined,
        "& .MuiOutlinedInput-root": {
          background: "#fff",
          fontSize: 13,
          borderRadius: "8px",
        },
      }}
    />
  );

  // Scope selector — one pill per scope, all on the bar so the current scope
  // is always visible. Exactly one is selected; "All groups" is the default.
  const scopes = (
    <Flx g={0.75} wrap role="group" aria-label="Show groups for">
      {SCOPE_OPTIONS.map((o) => (
        <ButtonBase
          key={o.key}
          aria-pressed={scope === o.key}
          onClick={() => setScope(o.key)}
          sx={pillSx(scope === o.key)}
        >
          {o.label}
        </ButtonBase>
      ))}
    </Flx>
  );

  // View toggles sit out on the bar: used as often as expand / collapse, and
  // their state reads at a glance.
  const toggles = (
    // One right-aligned cluster: Show missing types sits directly beside
    // expand / collapse.
    <Flx ac jc="flex-end" g={0.75} sx={{ ml: "auto" }}>
      <ShowMissingToggle
        count={hiddenCounts.types}
        checked={showMissing}
        onChange={setShowMissing}
      />
      <Flx ac g={0.75}>
        <Tooltip title="Expand all">
          <IconButton size="small" onClick={expandAll} sx={iconBtnSx}>
            <UnfoldMoreRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Collapse all">
          <IconButton size="small" onClick={collapseAll} sx={iconBtnSx}>
            <UnfoldLessRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Flx>
    </Flx>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* ---- sticky toolbar ---- */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: toolbarBg,
          pt: 1,
          pb: 1.5,
          borderBottom: `1px solid ${LINE_SOFT}`,
        }}
      >
        {compact ? (
          <Flx column g={1.25}>
            {search}
            {/* Scope pills and the view cluster share one row, wrapping
                only when the panel is too narrow for both. */}
            <Flx ac wrap g={1.25}>
              {scopes}
              {toggles}
            </Flx>
          </Flx>
        ) : (
          <Flx ac wrap g={1.25}>
            {search}
            {scopes}
            {toggles}
          </Flx>
        )}
      </Box>

      {/* ---- masonry grid ---- */}
      {visibleGroups.length ? (
        <Box
          sx={{
            mt: 2,
            columnWidth: 330,
            columnGap: "14px",
            "@media (max-width:720px)": {
              columnWidth: "auto",
              columnCount: 1,
            },
          }}
        >
          {visibleGroups.map(({ group, types }) => (
            <GroupCard
              key={group.name}
              group={group}
              visibleTypes={types}
              term={term}
              collapsed={collapsedGroups.has(group.name)}
              onToggleCollapsed={() => toggleGroup(group.name)}
              openTypes={openTypes}
              onToggleType={toggleType}
              onUpload={onUpload}
              onPreview={onPreview}
            />
          ))}
        </Box>
      ) : (
        <Flx column ac sx={{ py: 7.5, px: 2.5, textAlign: "center" }}>
          <Txt
            sx={{ fontSize: 15, fontWeight: 600, color: slate[900], mb: 0.5 }}
          >
            No matches
          </Txt>
          <Txt sx={{ fontSize: 13, color: INK_2 }}>
            Try a different search term, or clear the active filters above.
          </Txt>
        </Flx>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// The view (Documents tab → Document Group Coverage)
// ---------------------------------------------------------------------------
const DocGroupCoverageView = () => {
  const dispatch = useDispatch();
  const { model, stats } = useDocCoverageModel();
  const { onFilesAdded } = useUploadQueue();

  // ---- upload: one hidden picker, retargeted per type ---------------------
  // Chosen files land in the shared bottom-docked queue with the group/type
  // (and auto validity window) prefilled — same path as the Missing tab rows.
  const fileInputRef = useRef(null);
  const pendingTargetRef = useRef(null);
  const handleUpload = (docGroup, docType) => {
    pendingTargetRef.current = { docGroup, docType };
    fileInputRef.current?.click();
  };
  const handleFileInputChange = (e) => {
    const target = pendingTargetRef.current;
    const fileList = e.target.files;
    if (target && fileList?.length) {
      const days = getExpirationDaysForDocType(target.docType);
      onFilesAdded(
        Array.from(fileList).map((file) => ({
          id: uuidv4(),
          file,
          name: file.name,
          file_display_name: file.name,
          docGroup: target.docGroup,
          docType: target.docType,
          reportGuidelineExpirationDays: days ?? null,
        })),
      );
    }
    pendingTargetRef.current = null;
    e.target.value = ""; // allow re-selecting the same file
  };

  const handlePreview = (doc) => {
    if (doc) dispatch(filePreviewSetData(doc));
  };

  // The page frame is LoanTabShell — the same header standard as Contacts:
  // title + one-line subtitle on the page background, the stats on the right.
  // The legend sits right under the subtitle, in the header's title block.
  return (
    <LoanTabShell
      title="Document coverage"
      subTitle="Every document group and type for this loan at a glance — teal means files are uploaded against that type."
      subTitleContent={<DocCoverageLegend />}
      endContent={
        <Flx g={1.25} wrap>
          <Stat
            value={stats.groupsWith}
            of={stats.totalGroups}
            label="Groups with docs"
          />
          <Stat
            value={stats.typesWith}
            of={stats.totalTypes}
            label="Types with docs"
          />
          <Stat value={stats.files} label="Files uploaded" />
        </Flx>
      }
      bodySx={{ gap: 0 }}
    >
      <DocGroupCoverageBoard
        model={model}
        onUpload={handleUpload}
        onPreview={handlePreview}
      />
      {/* Single hidden picker, retargeted per type by the upload buttons. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileInputChange}
      />
    </LoanTabShell>
  );
};


const iconBtnSx = {
  width: 32,
  height: 32,
  borderRadius: "8px",
  border: `1px solid ${LINE}`,
  background: "#fff",
  color: INK_2,
  boxShadow: CARD_SHADOW,
  "&:hover": {
    color: teal[700],
    borderColor: teal[200],
    background: teal[100],
  },
};

export default DocGroupCoverageView;
